import React, { useContext, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { isAdminUser } from "../utils/roleUtils";
import { decorateUiItems } from "../utils/uiConfig";
import { getUiIcon } from "../utils/uiIcons";
import { findActiveTrail, pruneNavTree } from "../utils/navTree";
import {
  getRecentNavIds,
  getSidebarCollapsed,
  markRecentNavId,
  setSidebarCollapsed,
} from "../utils/navPrefs";
import Header from "./Header";
import Sidebar from "./Sidebar";
import "../pages/AdminHome.css";

function AppLayout() {
  const { user, uiConfig } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setCollapsed] = useState(getSidebarCollapsed);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const isAdmin = isAdminUser(user);
  const employeeId = user?.employeeId;

  const items = useMemo(() => {
    const withIcons = (list) =>
      list.map((item) => ({
        ...item,
        icon: getUiIcon(item.icon),
        children: Array.isArray(item.children) ? withIcons(item.children) : [],
      }));
    return pruneNavTree(withIcons(decorateUiItems(uiConfig?.sidebarItems || [], { isAdmin })));
  }, [isAdmin, uiConfig]);

  const currentPath = location.pathname + location.search;
  const activeTrail = useMemo(() => findActiveTrail(items, currentPath), [items, currentPath]);

  const [recentIds, setRecentIds] = useState(() => getRecentNavIds(employeeId));

  useEffect(() => {
    setRecentIds(getRecentNavIds(employeeId));
  }, [employeeId]);

  useEffect(() => {
    const currentId = activeTrail[activeTrail.length - 1];
    if (currentId) setRecentIds(markRecentNavId(employeeId, currentId));
  }, [employeeId, activeTrail]);

  const handleNavigate = (itemId) => {
    if (itemId.startsWith("/")) {
      navigate(itemId);
    }
  };

  const handleToggleSidebar = () => {
    setCollapsed((previous) => setSidebarCollapsed(!previous));
  };

  return (
    <>
      <Header
        onMenuToggle={() => setMobileSidebarOpen((previous) => !previous)}
        collapsed={sidebarCollapsed}
      />
      <div className="admin-layout">
        <Sidebar
          onNavigate={handleNavigate}
          collapsed={sidebarCollapsed}
          onToggle={handleToggleSidebar}
          mobileOpen={mobileSidebarOpen}
          onMobileClose={() => setMobileSidebarOpen(false)}
          items={items}
          activeTrail={activeTrail}
          recentIds={recentIds}
          employeeId={employeeId}
        />
        <main className={`admin-main ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
          <Outlet />
        </main>
      </div>
    </>
  );
}

export default AppLayout;
